/**
 * SP (system-points) budget — the single source of truth for the mech SP cap and
 * the SP consumed by a loadout. Both the **reducer wall** (`equip_system`'s
 * hard-reject gate, `reducer.ts`) and the **computed-stat display** (`compute.ts`'s
 * `ComputedMechStats.spMax`/`spUsed`) import from here, so the gate and the number
 * the UI shows can never drift — the same rules-live-once discipline as `budgets.ts`
 * (HASE/skill/talent caps) and `mounts.ts` (weapon fit).
 *
 * Why this lives in `@lancer-vtt/domain`, not `@lancer-vtt/rules`: the SP budget is a
 * *computed* value (frame stat + grit + HASE), but the reducer (in this package) is
 * the authoritative wall and can't import `@lancer-vtt/rules` (the dependency runs
 * rules → domain, never back). So the shared, pure, compendium-aware SP helpers live
 * down here where both the reducer and (one direction up) `compute.ts` can reach them.
 *
 * **SP-bonus layering is intentionally NOT applied here.** `spMax` is the canonical
 * COMP/CON base — `frame.sp + grit + floor(sys/2)` (matches `Mech.MaxSP`). `BonusId.SP`
 * bonuses (the `apply("sp", …)` wrapper in `compute.ts`) layer on top *for the display*;
 * the bonus stacking engine lives in `@lancer-vtt/rules` and stays there. No core item
 * grants an `sp` bonus (verified against `@massif/lancer-data`), so the gate (this base)
 * and the display are identical on all core content; a community LCP that grants one
 * would widen the *displayed* cap above the gate — a known, documented seam, not a bug.
 *
 * **`computeSpUsed` walks the COMPOSED mount array (`composedMounts.ts`), not
 * `mech.loadouts[…].mounts` directly** — a weapon/mod sitting in a core-bonus bonus mount
 * (the Improved Armament Flex mount) is real mech-wide SP cost and must count against the
 * budget just like a native-mount weapon. That's why `computeSpUsed` takes `pilot` —
 * `composedMounts` needs it to resolve which bonus mounts the pilot's held core bonuses
 * compose in.
 */
import type { MechData, PilotData } from "./types";
import { composedMounts, type CoreBonusCompendium } from "./composedMounts";

/**
 * The read-only compendium shape the SP helpers need: resolve a frame/system/weapon/mod
 * by id to read its `sp`, PLUS (via {@link CoreBonusCompendium}) a core bonus by id to
 * read its `bonuses` rows — `computeSpUsed` calls `composedMounts`, which needs that
 * tenant to resolve bonus-mount inclusion. Structurally typed (not the concrete
 * `Compendium` class) so this pure module stays free of a hard `@lancer-vtt/compendium`
 * value import — the reducer and `compute.ts` both pass their real `Compendium`, which
 * satisfies this.
 */
export type SpCompendium = CoreBonusCompendium & {
  getById(type: "frame", id: string): { stats?: { sp?: number } } | undefined;
  getById(type: "system", id: string): { sp?: number } | undefined;
  getById(type: "weapon", id: string): { sp?: number } | undefined;
  getById(type: "mod", id: string): { sp?: number } | undefined;
}

/** Pilot grit: `ceil(level / 2)`. (Duplicated tiny formula to keep this module dep-free.) */
function grit(level: number): number {
  return Math.ceil(level / 2);
}

/**
 * The SP **budget** a mech has: the canonical COMP/CON base
 * `frame.sp + grit + floor(sys/2)`. Returns `0` for a frameless mech (nothing to
 * spend SP on yet) or an unresolved frame. SP-bonus layering is the caller's job
 * (display only — see the module header).
 */
export function computeSpMax(
  mech: MechData,
  pilot: PilotData,
  compendium: SpCompendium,
): number {
  if (!mech.frameId) return 0;
  const frame = compendium.getById("frame", mech.frameId);
  const frameSp = frame?.stats?.sp;
  if (typeof frameSp !== "number") return 0;
  return frameSp + grit(pilot.level) + Math.floor(pilot.mechSkills.sys / 2);
}

/**
 * The SP **consumed** by a mech's active loadout: Σ over equipped weapons' `sp`
 * + each equipped weapon's attached mod's `sp` (charged THROUGH the host weapon —
 * COMP/CON's `MechWeapon.TotalSP = SP + (Mod ? Mod.SP : 0)`, not a standalone line)
 * + equipped systems' `sp`. Most core weapons carry no SP, so the weapon loop sums
 * ~0 today — SP is mostly a systems/mods cost — but a few weapons do (e.g. Gandiva
 * sp 1), so the loop stays. An item with a non-numeric `sp` contributes 0.
 *
 * Walks `composedMounts(loadout, pilot, compendium)` (see the module header) rather
 * than `loadout.mounts` directly, so a weapon/mod equipped in a core-bonus bonus mount
 * (e.g. Improved Armament's Flex mount) is charged its SP exactly like a native-mount
 * weapon — this is why `pilot` is a required parameter.
 */
export function computeSpUsed(mech: MechData, pilot: PilotData, compendium: SpCompendium): number {
  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  if (!loadout) return 0;
  let used = 0;

  // Equipped weapons (mount/slot-scoped, COMPOSED — native mounts + any bonus-mount
  // tail), each charging its own sp plus its attached mod's sp THROUGH the host weapon
  // (COMP/CON `MechWeapon.TotalSP`).
  for (const mount of composedMounts(loadout, pilot, compendium)) {
    for (const slot of mount.slots) {
      if (!slot.weaponId) continue;
      const weapon = compendium.getById("weapon", slot.weaponId);
      if (weapon && typeof weapon.sp === "number") used += weapon.sp;
      if (slot.modId) {
        const mod = compendium.getById("mod", slot.modId);
        if (mod && typeof mod.sp === "number") used += mod.sp;
      }
    }
  }

  // Equipped systems (loadout-scoped).
  for (const systemId of loadout.systems ?? []) {
    const system = compendium.getById("system", systemId);
    if (system && typeof system.sp === "number") used += system.sp;
  }

  return used;
}
